import { InfoIcon } from "lucide-react";
import React, { SetStateAction, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

interface Props {
  micAllowed: boolean;
  setMicAllowed: (value: boolean) => void;
  micMuted: boolean;
  setMicMuted: (value: SetStateAction<boolean>) => void;
  onCancel: () => void;
}

export default function MicPermissionHandler({ micAllowed, setMicAllowed, micMuted, setMicMuted, onCancel }: Props) {
  const [showDialog, setShowDialog] = useState(false);
  const [micAttempts, setMicAttempts] = useState(0);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const MAX_MIC_ATTEMPTS = 3;
  let toastId: any = '';

  async function checkMicPermission(): Promise<PermissionState> {
    try {
      const status = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      return status.state;
    } catch {
      return "prompt";
    }
  }

  async function requestMicAccess() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Clean up and set allowed
      stream.getTracks().forEach((track) => track.stop());
      setMicAllowed(true);
    } catch (error: any) {

      if (error.name === "NotAllowedError") {
        toastId = toast.error(
          "Microphone access is blocked by your browser or OS. Please allow it in system or Chrome settings."
          , {
            toastId
          }
        );
      } else if (error.name === "NotFoundError") {
        toastId = toast.error("No microphone found. Please connect one to continue.", {
          toastId
        });
      } else {
        toastId = toast.error("Failed to access microphone. Check browser/system settings.", {
          toastId
        });
      }

      setMicAllowed(false);
      setShowDialog(true);
    }
  }

  async function validateMicBeforeInterview() {
    const permissionState = await checkMicPermission();
    // const isMicMuted = !mediaStreamRef.current?.getAudioTracks()[0].enabled;
    // setMuted(isMicMuted);
    if (permissionState === "granted") {
      // Still validate browser-level mic access
      await requestMicAccess();
      return;
    }

    if (micAttempts >= MAX_MIC_ATTEMPTS) {
      showMicAccessError();
      setMicAllowed(false);
      return;
    }

    await requestMicAccess();
    setMicAttempts((prev) => prev + 1);
  }

  function showMicAccessError() {
    setMicAttempts(0);
    setShowDialog(true);
    toastId = toast.error(
      "Microphone access is blocked. Please enable it in Browser settings (browser://settings/content/microphone) or system settings.",
      {
        position: "top-right",
        autoClose: 6000,
        toastId
      }
    );
  }

  function handleRetry() {
    setShowDialog(false);
    validateMicBeforeInterview();
  }

  function handleCancel() {
    setShowDialog(false);
    setMicAttempts(0);
    setMicAllowed(false);
    onCancel();
    toast.dismiss(toastId);
  }

  useEffect(() => {
    async function getMicAccess() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;

        const isMicMuted = !stream.getAudioTracks().every(track => track.enabled);
        setMicMuted(isMicMuted);

        setMicAllowed(true);
        // toast.success(`Microphone is ${isMicMuted ? "muted" : "unmuted"} and access granted! 🎤`);
      } catch (err) {
        setMicAllowed(false);
        showMicAccessError();
      }
    }

    getMicAccess();

    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const unMuteMic = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = true; // reverse because we toggle
      });
      toast.info(`Microphone is now ${"unmuted"} 🎤`);
      setMicMuted(false);
    }
  };
  useEffect(() => {
    validateMicBeforeInterview();
  }, []);

  useEffect(() => {
    if (micAttempts > 0 && micAttempts < MAX_MIC_ATTEMPTS) {
      setShowDialog(true);
    } else if (micAttempts >= MAX_MIC_ATTEMPTS) {
      showMicAccessError();
    }
  }, [micAttempts]);

  useEffect(() => {
    if (micMuted && micAllowed) {
      setShowDialog(true);
      toastId = toast.error("Your microphone is muted. Please unmute it to continue.", {
        position: "top-right",
        autoClose: 6000,
        toastId
      });
    }
    else if (!micMuted && micAllowed) {
      setShowDialog(false);
      toast.dismiss(toastId);
    }
  }, [micMuted]);

  return (
    <>
      {!micAllowed && showDialog && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full p-6 mx-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Microphone Access or Sound Required
            </h2>
            <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
              This interview requires an active microphone. Please:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mb-4">
              <li>
                Click the lock <InfoIcon size={18} className="inline" /> icon next to the address bar
              </li>
              <li>Go to <strong>Site settings</strong></li>
              {
                !micAllowed && <>
                  <li>Allow microphone access for this site</li>
                  <li>Check Browser settings: <code>browser://settings/content/microphone</code></li>
                  <li>Ensure your microphone is connected and not disabled</li></>
              }
              {micMuted && <li><strong>Unmute your microphone and make sure it's picking up sound</strong></li>
              }            </ul>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
              >
                Cancel Interview
              </button>
              {micMuted && <div className="mic-controller">
                <button
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-accent-600 hover:bg-accent-700 text-white"
                  onClick={unMuteMic}>
                  {micMuted ? "Unmute Mic 🎤" : "Mute Mic 🔇"}
                </button>

              </div>}
              <button
                onClick={handleRetry}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-accent-600 hover:bg-accent-700 text-white"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
